import styled, { css } from 'styled-components'
import {
  BarryUIColorKey,
  BarryUISpacingKey,
  customPadding,
  theme,
} from '../../../theme'

const headingSizes = Object.keys(theme.typography.heading)
const bodySizes = Object.keys(theme.typography.text)
type HeadingSizes = (typeof headingSizes)[number]
type BodySizes = (typeof bodySizes)[number]

export type TypographyItemProps = {
  s?: HeadingSizes | BodySizes
  c?: BarryUIColorKey
  uc?: boolean // uppercase
  tc?: boolean
  lc?: boolean
  sp?: boolean // standard padding
  wba?: boolean // wraps text
  twb?: boolean // text-wrap: balance
  tdu?: boolean // text-decoration: underline
  tac?: boolean // text-align: center
  ta?: 'left' | 'right' | 'center' | 'justify' // text-align
  td?: 'underline' | 'strikethrough' // text-decoration
  ls?: string // letter-spacing
  lh?: number // line-height
  pad?: BarryUISpacingKey // pulls different padding values from theme
  padL?: BarryUISpacingKey // pulls different padding values from theme
  padR?: BarryUISpacingKey // pulls different padding values from theme
  padT?: BarryUISpacingKey // pulls different padding values from theme
  padB?: BarryUISpacingKey // pulls different padding values from theme
  padV?: BarryUISpacingKey // pulls different padding values from theme
  padH?: BarryUISpacingKey // pulls different padding values from theme
  fw?: 'regular' | 'semibold' | 'bold' | 'bolder' // pulls different padding values from theme
  ff?: 'Proxima Nova' | 'Space Grotesk' // pulls different padding values from theme
}

export const universalTypographyRules = css<TypographyItemProps>`
  ${customPadding}
  ${({ c }) => {
    if (!c) return ''
    const [parentKey, childKey] = c.split('-')
    const color = theme.semantics.zap?.[parentKey]?.[childKey]
    if (!color) return ''
    return `
      color: ${color};
      `
  }}
  ${({ uc }) => uc && 'text-transform: uppercase;'}
  ${({ tc }) => tc && 'text-transform: capitalize;'}
  ${({ lc }) => lc && 'text-transform: lowercase;'}
  ${({ wba }) => wba && 'word-break: break-all;'}
  ${({ twb }) => twb && 'text-wrap: balance;'}
  ${({ tdu }) => tdu && 'text-decoration: underline;'}
  ${({ td }) => td && `text-decoration: ${td};`}
  ${({ ta }) => ta && `text-align: ${ta};`}
  ${({ tac }) => tac && 'text-align: center;'}
  ${({ ls }) => ls && `letter-spacing: ${ls};`}
  ${({ fw }) => fw && `font-weight: ${theme.typography.weight[fw]};`}
  ${({ ff }) => ff && `font-family: ${ff};`}
`

export const h1Rules = css`
  font-size: ${({ theme }) =>
    theme.typography.heading['3XL'].size.expandedPlus}px;
  ${({ theme }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.heading['3XL'].size[bp]}px;
          line-height: ${theme.typography.heading['3XL'].lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }};
`

export const h2Rules = css`
  ${universalTypographyRules}
  ${({ theme }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.heading['2XL'].size[bp]}px;
          line-height: ${theme.typography.heading['2XL'].lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }};
`

export const h3Rules = css`
  ${universalTypographyRules}
  ${({ theme }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.heading['XL'].size[bp]}px;
          line-height: ${theme.typography.heading['XL'].lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }};
`

export const h4Rules = css`
  ${universalTypographyRules}
  ${({ theme }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.heading['L'].size[bp]}px;
          line-height: ${theme.typography.heading['L'].lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }};
`

export const h5Rules = css`
  ${universalTypographyRules}
  ${({ theme }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.heading['M'].size[bp]}px;
          line-height: ${theme.typography.heading['M'].lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }};
`

export const h6Rules = css`
  ${universalTypographyRules}
  ${({ theme }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.heading['S'].size[bp]}px;
          line-height: ${theme.typography.heading['S'].lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }};
`

export const pRules = css`
  ${universalTypographyRules}
  ${({ theme }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.text.M.size[bp]}px;
          line-height: ${theme.typography.text.M.lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }}
`

export const strongRules = css`
  ${universalTypographyRules}
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
  ${({ theme }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.text.M.size[bp]}px;
          line-height: ${theme.typography.text.M.lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }}
`

export const spanRules = css`
  ${universalTypographyRules}
  ${({ theme }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.text.M.size[bp]}px;
          line-height: ${theme.typography.text.M.lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }}
`

export const aRules = css`
  ${universalTypographyRules}
  ${({ theme }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.text.M.size[bp]}px;
          line-height: ${theme.typography.text.M.lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }}
`

export const H1 = styled.h1<TypographyItemProps>`
  ${customPadding}
  ${universalTypographyRules}
  font-family: 'Proxima Nova';
  font-size: ${({ s }) =>
    theme.typography.heading[s ?? '3XL'].size.expandedPlus}px;
  line-height: ${({ s }) =>
    theme.typography.heading[s ?? '3XL'].lineHeight.expandedPlus}px;
  ${({ s }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.heading[s ?? '3XL'].size[bp]}px;
          line-height: ${theme.typography.heading[s ?? '3XL'].lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }};
`
export const H2 = styled.h2<TypographyItemProps>`
  ${customPadding}
  ${universalTypographyRules}
  font-size: ${({ s }) =>
    theme.typography.heading[s ?? '2XL'].size.expandedPlus}px;
  line-height: ${({ s }) =>
    theme.typography.heading[s ?? '2XL'].lineHeight.expandedPlus}px;
  ${({ s, lh }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.heading[s ?? '2XL'].size[bp]}px;
          line-height: ${lh ?? theme.typography.heading[s ?? '2XL'].lineHeight[bp]
        }px;
        }
      `,
    )
    return rules.join('\n')
  }};
`
export const H3 = styled.h3<TypographyItemProps>`
  ${customPadding}
  ${universalTypographyRules}
  font-size: ${({ s }) =>
    theme.typography.heading[s ?? 'XL'].size.expandedPlus}px;
  line-height: ${({ s }) =>
    theme.typography.heading[s ?? 'XL'].lineHeight.expandedPlus}px;

  ${({ s }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.heading[s ?? 'XL'].size[bp]}px;
          line-height: ${theme.typography.heading[s ?? 'XL'].lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }};
`
export const H4 = styled.h4<TypographyItemProps>`
  ${customPadding}
  ${universalTypographyRules}
  font-size: ${({ s }) =>
    theme.typography.heading[s ?? 'L'].size.expandedPlus}px;
  line-height: ${({ s }) =>
    theme.typography.heading[s ?? 'L'].lineHeight.expandedPlus}px;
  ${({ s }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.heading[s ?? 'L'].size[bp]}px;
          line-height: ${theme.typography.heading[s ?? 'L'].lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }};
`
export const H5 = styled.h5<TypographyItemProps>`
  ${customPadding}
  ${universalTypographyRules}
  font-size: ${({ s }) =>
    theme.typography.heading[s ?? 'M'].size.expandedPlus}px;
  line-height: ${({ s }) =>
    theme.typography.heading[s ?? 'M'].lineHeight.expandedPlus}px;
  ${({ s }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.heading[s ?? 'M'].size[bp]}px;
          line-height: ${theme.typography.heading[s ?? 'M'].lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }};
`
export const H6 = styled.h6<TypographyItemProps>`
  ${customPadding}
  ${universalTypographyRules}
  font-size: ${({ s }) =>
    theme.typography.heading[s ?? 'S'].size.expandedPlus}px;
  line-height: ${({ s }) =>
    theme.typography.heading[s ?? 'S'].lineHeight.expandedPlus}px;
  ${({ s }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.heading[s ?? 'S'].size[bp]}px;
          line-height: ${theme.typography.heading[s ?? 'S'].lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }};
`
export const P = styled.p<TypographyItemProps>`
  ${customPadding}
  ${universalTypographyRules}
  font-size: ${({ s }) => theme.typography.text[s ?? 'M'].size.expandedPlus}px;
  line-height: ${({ s }) =>
    theme.typography.text[s ?? 'M'].lineHeight.expandedPlus}px;
  ${({ s, lh }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.text[s ?? 'M'].size[bp]}px;
          line-height: ${lh ?? theme.typography.text[s ?? 'M'].lineHeight[bp]
        }px;
        }
      `,
    )
    return rules.join('\n')
  }}
`

export const Span = styled.span<TypographyItemProps>`
  ${customPadding}
  ${universalTypographyRules}
  font-size: ${({ s }) => theme.typography.text[s ?? 'M'].size.expandedPlus}px;
  line-height: ${({ s }) =>
    theme.typography.text[s ?? 'M'].lineHeight.expandedPlus}px;
  ${({ s }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.text[s ?? 'M'].size[bp]}px;
          line-height: ${theme.typography.text[s ?? 'M'].lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }}
`

export const A = styled.a<TypographyItemProps>`
  ${customPadding}
  ${universalTypographyRules}
  font-size: ${({ s }) => theme.typography.text[s ?? 'S'].size.expandedPlus}px;
  line-height: ${({ s }) =>
    theme.typography.text[s ?? 'M'].lineHeight.expandedPlus}px;
  ${({ s }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.text[s ?? 'M'].size[bp]}px;
          line-height: ${theme.typography.text[s ?? 'M'].lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }}
`

export const Strong = styled.strong<TypographyItemProps>`
  ${customPadding}
  ${universalTypographyRules}
  font-size: ${({ s }) => theme.typography.text[s ?? 'M'].size.expandedPlus}px;
  line-height: ${({ s }) =>
    theme.typography.text[s ?? 'M'].lineHeight.expandedPlus}px;
  ${({ s }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.text[s ?? 'M'].size[bp]}px;
          line-height: ${theme.typography.text[s ?? 'M'].lineHeight[bp]}px;
        }
      `,
    )
    return rules.join('\n')
  }}
`
